export type TLitheSliceState = {
	meta: TSliceStateVariableBlueprint<TLitheMeta | null>;
	specsQuestions: TSliceStateVariableBlueprint<Array<TLitheQuestion> | null>;
	dataVizQuestions: TSliceStateVariableBlueprint<Array<TLitheQuestion> | null>;
	dataVizResults: TSliceStateVariableBlueprint<TLitheResultsBlueprint | null>;
	cashflowVizQuestions: TSliceStateVariableBlueprint<Array<TLitheQuestion> | null>;
	cashflowVizResults: TSliceStateVariableBlueprint<TLitheResultsBlueprint | null>;
	probsQuestions: TSliceStateVariableBlueprint<Array<TLitheQuestion> | null>;
	probsResults: TSliceStateVariableBlueprint<TLitheResultsBlueprint | null>;
	approvalInfo: TSliceStateVariableBlueprint<TLitheResultsBlueprint | null>;
	rfSpecsQuestions: TSliceStateVariableBlueprint<Array<TLitheQuestion> | null>;
	rawProbData: TSliceStateVariableBlueprint<any>;
};

export type TLitheCreationData = {
	litheName: string;
	assetClassId: string;
	litheCreationType: LitheCreationTypeEnum;
	litheScope: "global" | "tenant";
};

export enum LitheCreationTypeEnum {
	DATASET = "dataset",
	UG = "ug",
	MANUAL = "manual",
	PROGRAMMATIC = "programmatic",
}

export enum LitheScopeEnum {
	GLOBAL = "global",
	TENANT = "tenant",
}

export type TLitheMeta = TLitheCreationData & {
	litheId: string;
	datasetId?: string | null;
	datasetName?: string | null;
	allSteps: Array<LitheStepEnum>;
	currentStep: LitheStepEnum;
};

export enum LitheStepEnum {
	SPECS = "specs",
	UGCLEANDATA = "clean_data",
	UGFILTERS = "filters",
	UGTRANSDEF = "trans_def",
	UGFEATUREENG = "ug_feature_eng",
	UGREPLINES = "ug_replines",
	DATAVIZ = "proxy_data",
	ENGGFEATURES = "custom_columns",
	TRAININGDATA = "training_data",
	TRANSITION = "transition_config",
	REPLINES = "replines",
	MODELPARAMS = "model_params",
	CASHFLOWVIZ = "cashflow_viz",
	PROBS = "probs",
	RFSPECS = "rf_specs",
	RAWPROBS = "raw_probs",
	APPROVAL = "approval",
	LITHEAPPROVED = "lithe_approved",
}

export type TSliceStateVariableBlueprint<T> = {
	data: T;
	loading: boolean;
	error: any;
};

export type TLitheResultsBlueprint = {
	output: any;
	status: string;
};

export type TLitheQuestionRequestObject = {
	litheId: string;
	questionId: string;
	questionCategoryId: string;
	userInput: string;
	answerFormat?: "constant" | "sql";
	userInputType:
		| "natural_language"
		| "python_code"
		| "option_selection";
};

export type TLitheQuestionsStepPath =
	| "clean_data"
	| "filters"
	| "trans_def"
	| "ug_replines"
	| "ug_rf_specs";

export type TLitheQuestionApiResponse = {
	answer: unknown;
	default_value?: unknown;
	answer_type_info: {
		answer_type: string;
		constraints?: unknown;
		options?:
			| Array<{
					option_id?: string;
					display_name?: string;
			  }>
			| Record<string, unknown>;
	};
	category_id: string;
	is_genai_compatible: boolean;
	is_answered?: boolean;
	is_valid_answer?: boolean;
	is_processed: boolean;
	is_ui_editable?: boolean;
	error_msg?: string;
	python_transformation_code?: string;
	question: string;
	question_id: string;
	transform_description?: string;
	updated_time?: number;
};

export type TLitheQuestion = {
	question: string;
	questionId: string;
	answers: any;
	categoryId?: string;
	answerType: any;
	answer: any;
	defaultAnswer: any;
	validations?: any;
	isGenAIEnabled: boolean;
	pythonValue: string;
	naturalTextValue: string;
	label?: string;
	isProcessed: boolean;
	isAnswered: boolean;
	isValidAnswer: boolean;
	errorMessage?: string;
	isEditable?: boolean;
	updatedTime?: number;
};

export type TLitheEngineeringFeature = {
	id: string;
	featureName: string;
	description: string;
	formula: string;
	datatype: string;
	formulaType?: "sql";
	isDefault?: boolean;
	isApproved?: boolean;
	previewRows?: Array<
		Record<string, string | number | boolean | null>
	>;
};

export type TLitheEngineeringFeaturePreviewRequest = {
	colId: string;
	featureName: string;
	formula: string;
	userInputType: "sql_code";
};

export type TLitheEngineeringFeaturePreviewResponse = {
	isSuccess: boolean;
	message: string;
	litheId: string;
	featureId: string;
	featureName: string;
	formula: string;
	userInputType: "sql_code";
	previewRows: Array<
		Record<string, string | number | boolean | null>
	>;
};

export type TLitheEngineeringFeaturesResponse = {
	litheId: string;
	sectionTitle: string;
	sectionDescription: string;
	cardTitle: string;
	features: Array<TLitheEngineeringFeature>;
};

export type TLitheUgFeatureMetric = {
	metric: string;
	value: unknown;
};

export type TLitheUgFeatureHistogramBin = {
	bin_label: string;
	bin_min: number;
	bin_max: number;
	count: number;
	pct: number;
};

export type TLitheUgFeatureResult = {
	is_valid: boolean;
	error_msg?: string;
	summary?: Array<TLitheUgFeatureMetric>;
	hist_table?: Array<TLitheUgFeatureHistogramBin>;
};

export type TLitheUgFeatureEngineeringResponse = {
	status:
		| "NOT_YET_STARTED"
		| "IN_PROGRESS"
		| "COMPLETED"
		| "ERROR";
	updated_time: number;
	run_id?: string;
	started_at?: number;
	inputs_status: {
		state: "pending" | "valid" | "invalid";
		answered: Array<string>;
		invalid_values: Array<unknown>;
	};
	output?: {
		screen: "ug_feature_eng";
		selected: Array<string>;
		features: Record<string, TLitheUgFeatureResult>;
	};
	error_msg?: string;
	is_stale?: boolean;
};

export type TLitheItem = {
	id: string;
	name: string;
	uploadTime: string;
	userId: string;
	tenant: string;
	status: string;
	assetClass: string;
	createdBy?: string;
	lithe_creation_type?: string;
};

export type TLitheSearchResponseProps = {
	lithes: Array<TLitheItem>;
	count: number;
	error?: any;
};
